import type { ReactNode } from "react";
import { useSyncExternalStore } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { GlofoxBrandLogo } from "@/components/portal/GlofoxBrandLogo";
import { useColorTheme } from "@/hooks/useColorTheme";
import { adminInitials } from "@/lib/api/admin-access-request-labels";
import {
  clearAdminAuthSession,
  getAdminAuthSessionSnapshot,
  subscribeAdminAuthSession,
} from "@/lib/auth/admin-session";
import { withBasePath } from "@/lib/portal/base-path";
import { SAME_ORIGIN_NEW_TAB_REL } from "@/lib/portal/same-origin-new-tab";

import "@/styles/admin-panel.css";

export type AdminNavKey = "control-panel" | "metrics" | "users";

type AdminShellProps = {
  activeNav: AdminNavKey;
  title: string;
  description?: string;
  titleTestId?: string;
  afterMain?: ReactNode;
  children: ReactNode;
  testId?: string;
};

function spaHomeHref(): string {
  const prefix = withBasePath("/").replace(/\/$/, "") || "";
  return `${prefix}/#/`;
}

export function AdminShell({
  activeNav,
  title,
  description,
  titleTestId,
  afterMain,
  children,
  testId,
}: AdminShellProps) {
  const navigate = useNavigate();
  const { theme, setTheme } = useColorTheme();
  const session = useSyncExternalStore(
    subscribeAdminAuthSession,
    getAdminAuthSessionSnapshot,
    getAdminAuthSessionSnapshot,
  );

  if (!session) {
    return <Navigate to="/admin/login" replace />;
  }

  function handleLogout() {
    clearAdminAuthSession();
    navigate("/admin/login", { replace: true });
  }

  return (
    <div className="admin-panel" data-testid={testId}>
      <div className="shell">
        <nav className="topbar" aria-label="Admin navigation">
          <div className="tb-brand">
            <GlofoxBrandLogo to="" testId="admin-glofox-logo" />
            <span className="tb-section">Admin</span>
            <span className="tb-pill">Internal</span>
          </div>
          <div className="tb-right">
            <label className="admin-theme-toggle" title="Toggle color theme">
              <input
                type="checkbox"
                role="switch"
                checked={theme === "dark"}
                aria-label="Dark mode"
                data-testid="admin-theme-toggle"
                onChange={(event) =>
                  setTheme(event.target.checked ? "dark" : "light")
                }
              />
              <span aria-hidden>
                <i
                  className={`ti ${theme === "dark" ? "ti-sun" : "ti-moon"}`}
                />
              </span>
            </label>
            <div className="tb-avatar" aria-hidden="true">
              {adminInitials(session.user.name)}
            </div>
            <span className="tb-user" data-testid="admin-home-user">
              {session.user.name}
            </span>
            <button
              type="button"
              className="tb-logout"
              data-testid="admin-logout"
              onClick={handleLogout}
            >
              Log out
            </button>
          </div>
        </nav>

        <aside className="sidebar" aria-label="Sidebar navigation">
          <div className="sb-sect">Navigation</div>
          <Link
            className={`sb-link${activeNav === "control-panel" ? " on" : ""}`}
            to="/admin/control-panel"
            data-testid="admin-nav-control-panel"
          >
            <i className="ti ti-inbox" aria-hidden="true" /> Access requests
          </Link>
          <Link
            className={`sb-link${activeNav === "metrics" ? " on" : ""}`}
            to="/admin/metrics"
            data-testid="admin-nav-metrics"
          >
            <i className="ti ti-chart-bar" aria-hidden="true" /> Metrics
          </Link>
          <Link
            className={`sb-link${activeNav === "users" ? " on" : ""}`}
            to="/admin/users"
            data-testid="admin-nav-users"
          >
            <i className="ti ti-users" aria-hidden="true" /> Portal users
          </Link>
          <div className="sb-sect">Links</div>
          <a
            className="sb-link"
            href={spaHomeHref()}
            target="_blank"
            rel={SAME_ORIGIN_NEW_TAB_REL}
            data-testid="admin-link-dev-portal"
          >
            <i className="ti ti-external-link" aria-hidden="true" /> Dev portal
          </a>
        </aside>

        <main className="main">
          <div className="page-hdr">
            <h1 data-testid={titleTestId ?? "admin-page-title"}>{title}</h1>
            {description ? <p>{description}</p> : null}
          </div>
          {children}
        </main>
        {afterMain}
      </div>
    </div>
  );
}
